import { Boxes, Calendar, ExternalLink, PauseCircle, ShoppingCart, User, Warehouse } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';

import { BundleState } from 'data/__generated__';

import { formatDateForDisplay } from 'helpers/dateTimes';

import { ContainerTypeIcon } from './ContainerTypeTag';
import { BundleTypeIcon } from './TypeTag';
import { getBundleDeliveries, type BundleRow } from '../constants/bundlesColumns';

export const BundleTrelloItem = ({
    bundle,
    isSelected,
    toggleBundleSelection,
}: {
    bundle: BundleRow;
    isSelected: boolean;
    toggleBundleSelection: (bundleId: string) => void;
}) => {
    const {
        _id: bundleId,
        boxes,
        containerType,
        number,
        numberOfReferences,
        numberOfUnits,
        operatorName,
        scheduledPreparationDate,
        stateHistory,
        type,
        warehouseColumn,
    } = bundle;

    const deliveries = getBundleDeliveries(bundle);
    const hasBeenPaused = stateHistory.some((entry) => entry.state === BundleState.Paused);

    return (
        <div
            className={cn(
                'overflow-hidden rounded-[4px] border border-border bg-filter shadow-xs transition-shadow hover:shadow-sm',
                isSelected && 'ring-2 ring-primary',
            )}
        >
            {/* Le flux et le contenant sont repliés en icônes : ils tiennent dans l'en-tête, à côté du
                numéro, au lieu de coûter une ligne de pastilles au corps de la tuile. */}
            <div className="flex items-center gap-2 border-b border-border/60 px-2.5 py-1.5">
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleBundleSelection(bundleId)}
                    aria-label="Sélectionner le bundle"
                    className="shrink-0"
                />
                <span className="min-w-0 truncate text-sm font-extrabold text-foreground">N°{number}</span>
                <BundleTypeIcon type={type} />
                <ContainerTypeIcon type={containerType} />
                <Link
                    to={`/bundle/${bundleId}`}
                    className="ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Ouvrir le bundle"
                >
                    <ExternalLink className="size-3.5" />
                </Link>
            </div>

            <div className="flex flex-col gap-1 px-2.5 py-1.5">
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                        <Calendar className="size-3.5 shrink-0" />
                        {formatDateForDisplay(scheduledPreparationDate, { displayDay: true })}
                    </span>
                    <span className="inline-flex items-center gap-1" title="Nombre de caisses">
                        <Boxes className="size-3.5 shrink-0" />
                        {boxes.length}
                    </span>
                    <span className="inline-flex items-center gap-1" title="Références et unités">
                        <ShoppingCart className="size-3.5 shrink-0" />
                        {numberOfReferences} réfs · {numberOfUnits} u.
                    </span>
                </div>

                {warehouseColumn || operatorName ? (
                    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground">
                        {warehouseColumn ? (
                            <span className="inline-flex items-center gap-1" title="Entrepôt">
                                <Warehouse className="size-3.5 shrink-0" />
                                {warehouseColumn.name}
                            </span>
                        ) : null}
                        {operatorName ? (
                            <span className="inline-flex items-center gap-1" title="Opérateur.ice">
                                <User className="size-3.5 shrink-0" />
                                {operatorName}
                            </span>
                        ) : null}
                    </div>
                ) : null}

                {/* Pour qui on prépare : une ligne par livraison servie, le nombre de ses caisses entre
                    parenthèses, et le lien vers la livraison — comme la colonne « Caisses » du tableau. */}
                {deliveries.length ? (
                    <div className="flex flex-col gap-0.5 border-t border-border/60 pt-1">
                        {deliveries.map(({ deliveryId, name, numberOfBoxes }) => (
                            <Link
                                key={deliveryId}
                                to={`/delivery/${deliveryId}`}
                                title={name}
                                className="truncate text-xs text-foreground hover:underline"
                            >
                                {name} <span className="text-muted-foreground">({numberOfBoxes})</span>
                            </Link>
                        ))}
                    </div>
                ) : null}

                {hasBeenPaused ? (
                    <div className="inline-flex items-center gap-1 text-xs text-destructive">
                        <PauseCircle className="size-3.5 shrink-0" />A été mis en pause
                    </div>
                ) : null}
            </div>
        </div>
    );
};
